import { Search } from '@mui/icons-material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { Card, Grid } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  SearchBar,
  WsAutoComplete,
  WsButton,
  WsColors,
  WsLocationSearch,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import { useTokenContext } from '../../../context/TokenContext';
import { useWorkspaceContext } from '../../../context/WorkspaceContext/WorkspaceContext';
import { clientsAmplitudeEvents } from '../../../features/clientDetails/helper/clientDetailsHelper';
import { getFilterValue } from '../../../features/notes/helper/NotesHelper';
import SmartFilter from '../../../features/submissionsNew/components/filters/smart/SmartFilter';
import { ClientAction, useClientAuthz } from '../../../hooks/useClientAuthz';
import { IFilters, StyledBox, initialLocation } from '../helper/ClientListingHelper';

export interface IClientFilters {
  requireHeaderBanner: boolean;
  searchQuery: IFilters;
  clear: () => void;
  getUpdatedFiltersFromFilters: (data) => void;
  clientStatuses: string[];
  tags: string[];
  defaultFilter: IFilters;
}

const ClientFilters = ({
  requireHeaderBanner,
  searchQuery,
  clear,
  getUpdatedFiltersFromFilters,
  clientStatuses,
  tags,
  defaultFilter,
}: IClientFilters) => {
  const getFinalStatus = () =>
    searchQuery.status?.length ? searchQuery.status : defaultFilter.status ?? [];

  const getFinalTags = () =>
    searchQuery.tags?.length ? searchQuery.tags : defaultFilter.tags ?? [];

  const methods = useForm<IFilters>({
    defaultValues: {
      ...defaultFilter,
      ...searchQuery,
      status: getFinalStatus(),
      tags: getFinalTags(),
    },
  });

  const { handleSubmit, getValues, setValue, reset } = methods;
  const token = useTokenContext();
  const navigate = useNavigate();
  const currentUser = useWorkspaceContext();
  const isAuthorizedAddClient = useClientAuthz(ClientAction.ADD_CLIENT_DETAILS);

  const [statusValue, setStatusValue] = useState<string[]>(getFinalStatus());
  const [tagsValue, setTagsValue] = useState<string[]>(getFinalTags());

  useEffect(() => {
    const updatedStatus = getFinalStatus();
    const updatedTags = getFinalTags();
    setStatusValue(updatedStatus);
    setTagsValue(updatedTags);
    setValue('status', updatedStatus);
    setValue('tags', updatedTags);
  }, [searchQuery, defaultFilter, setValue]);

  const navigateToAddClientOptions = () => {
    clientsAmplitudeEvents('client_add', 'Client Creation', currentUser);
    navigate('/clients/addclientoptions');
  };

  const handleClear = () => {
    const status = defaultFilter.status ?? [];
    const tags = defaultFilter.tags ?? [];

    reset({ ...defaultFilter, status, tags });
    setStatusValue(status);
    setTagsValue(tags);
    clear();
  };

  const handleFilters = useCallback(() => {
    getUpdatedFiltersFromFilters({
      ...searchQuery,
      ...getValues(),
      location: getValues('location') ?? initialLocation[0],
    });
  }, [searchQuery, getUpdatedFiltersFromFilters, getValues]);

  const onSearch = () => handleFilters();

  const onStatusSelection = (statusInput: string[]) => {
    setValue('status', statusInput);
    setStatusValue(statusInput);
  };

  const onTagsSelection = (tagsInput: string[]) => {
    setValue('tags', tagsInput);
    setTagsValue(tagsInput);
  };

  return (
    <>
      {requireHeaderBanner && (
        <StyledBox mt={2}>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSearch)}>
              <Grid container bgcolor={WsColors.White}>
                <Grid container justifyContent="flex-start" spacing={1} xs={7}>
                  <Grid item xs={8}>
                    <Card sx={{ p: 0.5, height: 32 }}>
                      <SearchBar name="search" placeholder="Search Clients" />
                    </Card>
                  </Grid>
                  <Grid item xs={4} gap={1} display="flex">
                    <WsButton
                      className="advance-filter"
                      variant="outlined"
                      size="medium"
                      type="reset"
                      onClick={handleClear}
                    >
                      Clear
                    </WsButton>
                    <WsButton
                      className="advance-filter"
                      variant="contained"
                      size="medium"
                      type="submit"
                      onClick={onSearch}
                      startIcon={<Search />}
                    >
                      Search
                    </WsButton>
                  </Grid>
                </Grid>
                <Grid container justifyContent="flex-end" alignItems="center" xs={5} spacing={1}>
                  {requireHeaderBanner && (currentUser.owns || isAuthorizedAddClient) && (
                    <Grid item>
                      <WsButton
                        fullWidth
                        onClick={navigateToAddClientOptions}
                        variant="contained"
                        size="large"
                        startIcon={<AddOutlinedIcon />}
                      >
                        Add Client
                      </WsButton>
                    </Grid>
                  )}
                </Grid>
                <Grid container justifyContent="flex-start" alignItems="center" pt={2} spacing={1}>
                  <Grid item>
                    <WsTypography
                      variant={WsTypoVariants.SUB_TITLE}
                      bold
                      color={WsTypoColors.BLACK}
                    >
                      Applied Filter:
                    </WsTypography>
                  </Grid>
                  <Grid item>
                    <SmartFilter
                      filterName="Client Location"
                      onClick={onSearch}
                      filterValue={getFilterValue('location', methods)}
                    >
                      <WsLocationSearch
                        name="location"
                        multiSelect={false}
                        placeHolder=""
                        size="small"
                        wisestepApi={true}
                        token={token.token}
                        authorization={token.auth}
                      />
                    </SmartFilter>
                  </Grid>
                  <Grid item>
                    <SmartFilter
                      filterName="Client status"
                      onClick={onSearch}
                      filterValue={getFilterValue('status', methods)}
                    >
                      <WsAutoComplete
                        key={`status-${statusValue.join(',')}`}
                        onTagsChange={onStatusSelection}
                        placeholder="Eg, Michael"
                        initialValue={statusValue}
                        value={statusValue}
                        optionsInStringArray={clientStatuses}
                        multiple={true}
                        freeSolo={true}
                        name="status"
                        size="small"
                      />
                    </SmartFilter>
                  </Grid>
                  <Grid item>
                    <SmartFilter
                      filterName="Client Tags"
                      onClick={onSearch}
                      filterValue={getFilterValue('tags', methods)}
                    >
                      <WsAutoComplete
                        key={`tags-${tagsValue.join(',')}`}
                        onTagsChange={onTagsSelection}
                        placeholder="Eg, Michael"
                        initialValue={tagsValue}
                        value={tagsValue}
                        optionsInStringArray={tags}
                        multiple={true}
                        freeSolo={true}
                        name="tags"
                        size="small"
                      />
                    </SmartFilter>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          </FormProvider>
        </StyledBox>
      )}
    </>
  );
};

export default ClientFilters;
